import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import moment from 'moment';
import { IconButton, Modal, Paper, Typography } from '@material-ui/core';
import { FaUsers, FaStopCircle } from 'react-icons/fa';

import API from 'utils/api';
import { useSources } from 'hooks';
import { ConfirmDialog, Tooltip } from 'components';
import { VS } from 'components/Icons';
import { UrlBox } from '..';
import { call } from 'utils';
import DataList from '../../../Analytics/components/DataList';

import styles from './EventHeader.module.scss';

const EventHeader = forwardRef(({ event = {}, onClose, showHost }, ref) => {
  const { isGlobal, name, active, channel = {}, game, host } = event;

  const [dialog, setDialog] = useState({});

  const [webUsers, setWebUsers] = useState(null);
  const [mobileUsers, setMobileUsers] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  const sources = useSources();

  useImperativeHandle(ref, () => ({
    setOnlineUsers: users => {
      if (Array.isArray(users) && users.length) {
        API.getUserInteractions({ users }, sources).then(({ data }) =>
          setWebUsers(
            data && Array.isArray(data.docs) && data.docs.length ? data.docs : null
          )
        );
      } else {
        setWebUsers(null);
      }
    },
  }));

  useEffect(() => {
    if (event._id) {
      let count = 0;
      let seqOfEmpty = 0;

      const timer = setInterval(() => {
        const seq = count % 18;

        ++count;

        if (seq > 2) {
          API.checkMobileConnection(event._id, sources).then(({ data }) => {
            if (Array.isArray(data) && data.length) {
              setMobileUsers(data);
              seqOfEmpty = 0;
            } else {
              seqOfEmpty++;
              if (seqOfEmpty === 3) {
                setMobileUsers(null);
                seqOfEmpty = 0;
              }
            }
          });
        } else if (seq === 0) {
          API.sendPushMobileConnected(event._id, sources);
        }
      }, 2000);

      return () => {
        clearInterval(timer);
      };
    }
  }, [event._id]);

  useEffect(() => {
    setOnlineUsers(Array.from(new Set([...(webUsers || []), ...(mobileUsers || [])])));
  }, [webUsers, mobileUsers]);

  const renderTitles = () => {
    if (isGlobal) {
      return (
        <div className={styles.titles}>
          <Typography variant="subtitle2">Global Messages</Typography>
          <Typography variant="h5">{`${channel.name} channel`}</Typography>
        </div>
      );
    }

    return (
      <div className={styles.titles}>
        <div className={styles.title}>
          <Typography variant="h5">{name}</Typography>

          {active ? (
            <IconButton color="primary" onClick={() => setDialog({ confirm: true })}>
              <Tooltip title="Click to close this event" placement="top">
                <FaStopCircle />
              </Tooltip>
            </IconButton>
          ) : null}
        </div>

        <div className={styles.title}>
          <Typography variant="subtitle2">{`${channel.name} channel`}</Typography>
          <UrlBox title="Event chat links" path={`/webapp/${event._id}`} />
        </div>
      </div>
    );
  };

  const renderGamePanel = () => {
    if (game) {
      const { home, away, homePosition, leagueName, scheduled } = game;
      const reverse = homePosition === 'right';

      const renderTeam = team => {
        const { name = '', logo } = team || {};

        return (
          <div className={styles.team}>
            <div
              className={styles.logo}
              style={logo ? { backgroundImage: `url(${logo})` } : undefined}
            />

            <Typography className={styles.name}>{name}</Typography>
          </div>
        );
      };

      return (
        <div className={styles.game}>
          <Typography variant="body2">{leagueName}</Typography>

          <div className={styles.panel}>
            <div className={styles.bg}></div>
            <div className={styles.fg}>
              <div className={styles.teams}>
                {renderTeam(reverse ? away : home)}
                <div className={styles.vs}>
                  <VS />
                </div>
                {renderTeam(reverse ? home : away)}
              </div>
            </div>
          </div>

          <Typography variant="caption">{moment(scheduled).format('llll')}</Typography>
        </div>
      );
    }

    return null;
  };

  const renderOnlineUsers = () => {
    return (
      <div className={styles.users}>
        <div>
          <IconButton
            color="primary"
            onClick={() => setDialog({ online: true })}
            disabled={!onlineUsers.length}
          >
            <Tooltip title="Click to show online users" placement="top">
              <FaUsers />
            </Tooltip>
          </IconButton>

          <Typography variant="h6" style={{ marginLeft: 12 }}>
            {onlineUsers.length.toLocaleString()}
          </Typography>
        </div>

        {showHost && host ? (
          <div className={styles.host}>
            <Typography variant="caption">host:</Typography>
            <Typography variant="subtitle2">{host.fullname}</Typography>
          </div>
        ) : null}
      </div>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <>
      <Paper>
        <div className={styles.header}>
          {renderTitles()}
          {renderGamePanel()}
          {renderOnlineUsers()}
        </div>
      </Paper>

      <Modal open={!!dialog.online} onClose={onCloseDialog} className={styles.modal}>
        <div>
          <DataList listUsersInteraction={onlineUsers} />
        </div>
      </Modal>

      <ConfirmDialog
        open={!!dialog.confirm}
        onClose={async yes => {
          onCloseDialog();

          if (yes) {
            await API.stopChannelEvent(event._id, sources);
            call(onClose);
          }
        }}
        title="Attention"
        message={[
          'Stopping this Event will make it inaccessible.',
          'Do you wish to continue?',
        ]}
      />
    </>
  );
});

export default EventHeader;
